import React from 'react'
import {Nullable} from '../../../../app/TypeHelpers'
import {Moment} from 'moment'
import {convertToNullableMoment} from '../../helpers/helpers'
import {Table, TableBody, TableCell, TableRow} from '@mui/material'
import {StyledTableCaption} from '../../../../styledComponents/adminPage/Table'
import {formatNumberWithCommas} from '../../../../formatters/formatNumberWithCommas'
import FacilityForReservation from '../../../facilities/FacilityForReservation'
import {AdminReserveLocationState, ReservationInfo} from '../AdminReserveInterfaces'
import {PaymentInformation} from '../../ReserveFacilityModel'
import lsHelper from '../AdminReserveLocationStateHelper'
import {LeftMarginLink} from '../AdminReserveStyledComponent'
import {BoldSpan} from '../../../../styledComponents/StyledSpan'
import {FacilityRentalType, ReservationClass} from '../../ReservationInterfaces'
import {FixedWidthTableRowName} from './ARDetailsView'
import EventEmitterExAdmin from '../../../shared/pageBaseComponents/EventEmitterExAdmin'
import PPErrorDisplaySpan from '../../../shared/forms/PPErrorDisplaySpan'
import styled from 'styled-components'
import {FontWeights} from '../../../../styledComponents/Constants'
import {NavigateFunction} from 'react-router-dom'

interface CommonProps {
    facilityData: FacilityForReservation
    reservationInfo: ReservationInfo
    paymentInfo: PaymentInformation
}

interface PropsForConfirm extends CommonProps {
    type: 'confirm'
    condominiumCd: string
    navigate: NavigateFunction
    eventEmitter?: EventEmitterExAdmin
    locationState: AdminReserveLocationState
}

interface PropsForComplete extends CommonProps {
    type: 'complete'
    eventEmitter?: undefined
}

type Props = PropsForConfirm | PropsForComplete

const ARFacilityView: React.FC<Props> = props => {
    const facilityChargeItemIndex = props.paymentInfo.items
        .findIndex(item => item.id === null)

    const {reserveType, startDate, reservationTitle} = props.reservationInfo
    const charge = (facilityChargeItemIndex !== -1)
        ? props.paymentInfo.items[facilityChargeItemIndex].charge
        : 0
    if (charge === null) {
        throw new Error('illegal charge value')
    }

    let startDateTimeString: string
    let endDateTimeString: string
    if (props.reservationInfo.type === FacilityRentalType.OVERNIGHT) {
        const {stayCount} = props.reservationInfo
        const {overnightFacilitySetting} = props.facilityData
        if (overnightFacilitySetting === null) {
            throw Error('OvernightFacilitySetting is null')
        }
        if (stayCount === null) {
            throw Error('StayCount is null')
        }
        const startDateTimeMoment: Nullable<Moment> =
            convertToNullableMoment(startDate, overnightFacilitySetting.checkinTime)
        const endDateMoment: Nullable<Moment> =
            convertToNullableMoment(startDate, overnightFacilitySetting.checkoutTime)
        if (startDateTimeMoment === null) {
            throw Error('StartDate is null')
        }
        if (endDateMoment === null) {
            throw Error('EndDate is null')
        }
        const endDateTimeMoment = endDateMoment.clone().add(stayCount, 'day')
        startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
    } else {
        const {startTime, endDate, endTime} = props.reservationInfo
        const startDateTimeMoment: Nullable<Moment> =
            convertToNullableMoment(startDate, startTime)
        const endDateTimeMoment: Nullable<Moment> =
            convertToNullableMoment(endDate, endTime)
        if (startDateTimeMoment === null) {
            throw Error('StartDate is null')
        }
        if (endDateTimeMoment === null) {
            throw Error('EndDate is null')
        }
        startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
    }

    const classificationString = props.reservationInfo.classification

    return (<Table>
        <StyledTableCaption>
            予約施設・日時
            {
                props.type === 'confirm' &&
                <LeftMarginLink onClick={() => {
                    props.navigate(
                        `/admin/condominiums/${props.condominiumCd}/${classificationString}-reserve/select-facility`,
                        {
                            replace: true,
                            state: lsHelper.toObject(props.locationState),
                        },
                    )
                }}>修正する</LeftMarginLink>
            }
        </StyledTableCaption>
        <TableBody>
            <TableRow>
                <FixedWidthTableRowName>予約施設</FixedWidthTableRowName>
                <TableCell>
                    <BoldSpan>
                        {props.facilityData.facilityName}
                    </BoldSpan>
                </TableCell>
            </TableRow>
            <TableRow>
                <FixedWidthTableRowName>予約種別</FixedWidthTableRowName>
                <TableCell>
                    <BoldSpan>
                        {reserveType.label}
                    </BoldSpan>
                </TableCell>
            </TableRow>
            <TableRow>
                <FixedWidthTableRowName>予約タイトル</FixedWidthTableRowName>
                <TableCell>
                    <ErrorDisplayBoldSpan
                        eventEmitter={props.eventEmitter}
                        eventKeys={['reservationTitle']}
                    >
                        {reservationTitle}
                    </ErrorDisplayBoldSpan>
                </TableCell>
            </TableRow>
            <TableRow>
                <FixedWidthTableRowName>利用日時</FixedWidthTableRowName>
                <TableCell>
                    <BoldSpan>
                        {`${startDateTimeString} 〜 ${endDateTimeString}`}
                    </BoldSpan>
                </TableCell>
            </TableRow>
            <TableRow>
                <FixedWidthTableRowName>施設利用料</FixedWidthTableRowName>
                <TableCell>
                    <BoldSpan>
                        {
                            props.reservationInfo.classification === ReservationClass.AGENT
                                ? formatNumberWithCommas(charge || 0)
                                : 0
                        }円
                    </BoldSpan>
                </TableCell>
            </TableRow>
        </TableBody>
    </Table>)
}

export default ARFacilityView

export const ErrorDisplayBoldSpan = styled(PPErrorDisplaySpan)`
  font-weight: ${FontWeights.Bold};
`
